/**
 * polyfill — co starší Safari na tabletu neumí a appka to potřebuje.
 *
 * Tom, 14. 9. 2026 večer: iPad u vchodu na v52.126 ukázal u HACCP
 * „Dokument se nepodařilo načíst", zatímco Mac tutéž směrnici kreslí.
 * pdf.js 5 volá `Promise.withResolvers()`, které má Safari až od 17.4
 * (jaro 2024) — starší iPad tedy spadne dřív, než PDF vůbec otevře.
 * Doplňuje se jen to, co chybí; kde už metoda je, nesahá se na ni.
 *
 * Importuje se jako PRVNÍ řádek `main.tsx`, ať je na místě dřív než
 * kterýkoli modul, který ji volá.
 */

type SPromise = { withResolvers?: <T>() => { promise: Promise<T>; resolve: (v: T | PromiseLike<T>) => void; reject: (d?: unknown) => void } };

const P = Promise as unknown as SPromise;
if (typeof P.withResolvers !== 'function') {
  P.withResolvers = <T>() => {
    let resolve!: (v: T | PromiseLike<T>) => void;
    let reject!: (d?: unknown) => void;
    const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}

/*
 * structuredClone — Safari až od 15.4 (březen 2022). Appka ho volá na
 * čtyřiceti místech (kopie stavu před zápisem), takže na starším iPadu
 * spadne první obrazovka, která si kopíruje data — a zůstane šedé nic.
 * Náhrada přes JSON stačí: appka klonuje prostá data (pole, objekty,
 * čísla, řetězce). Datum by se z ní vrátilo jako text — proto se to
 * nepoužívá nikde, kde by to vadilo, a na moderním prohlížeči se sem
 * vůbec nesáhne.
 */
const G = globalThis as unknown as { structuredClone?: <T>(v: T) => T };
if (typeof G.structuredClone !== 'function') {
  G.structuredClone = <T>(v: T): T => (v === undefined ? v : JSON.parse(JSON.stringify(v)) as T);
}

/* Array.prototype.at / String.prototype.at — Safari až od 15.4. */
type SAt = { at?: (i: number) => unknown };
for (const proto of [Array.prototype as unknown as SAt, String.prototype as unknown as SAt]) {
  if (typeof proto.at !== 'function') {
    proto.at = function at(this: { length: number; [i: number]: unknown }, i: number) {
      const n = Math.trunc(i) || 0;
      const idx = n < 0 ? this.length + n : n;
      return idx < 0 || idx >= this.length ? undefined : this[idx];
    };
  }
}

/*
 * AbortSignal.timeout — Safari až od 16 (2022). Používá se u krátkého
 * dotazu na zdraví serveru; bez náhrady by tahle větev shodila hlášku,
 * která má naopak pomoct.
 */
type SAbort = { timeout?: (ms: number) => AbortSignal };
const AS = AbortSignal as unknown as SAbort;
if (typeof AS.timeout !== 'function') {
  AS.timeout = (ms: number): AbortSignal => {
    const c = new AbortController();
    setTimeout(() => c.abort(new DOMException('TimeoutError', 'TimeoutError')), ms);
    return c.signal;
  };
}

export {};
